<template>
  <v-row
    justify="space-around"
  >
    <v-col
      cols="12"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Météo -->
      <stats-template
        :is-loading="isLoading.mood"
        :has-error="hasError.mood"
        :error-msg="errorMsg"
        icon="mdi-weather-partly-cloudy"
        :title="$t(title.mood)"
        :value="averageMood"
        :sub-text="numberOfMoodAnsweredString"
        :color="color"
      />
    </v-col>
    <v-col
      cols="12"
      :xl="gridSpacing.xl"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Mercis envoyés -->
      <stats-template
        :is-loading="isLoading.superSmiles"
        :has-error="hasError.superSmiles"
        :error-msg="errorMsg"
        icon="mdi-sticker-emoji"
        :title="numberOfSuperSmilesSentTitle"
        :value="numberOfSuperSmilesSent"
        :color="color"
      />
    </v-col>
    <v-col
      cols="12"
      :xl="gridSpacing.xl"
      :lg="gridSpacing.lg"
      :md="gridSpacing.md"
      :sm="gridSpacing.sm"
    >
      <!-- Médaillon Qualité de vie au travail -->
      <stats-template
        :is-loading="isLoading.question"
        :has-error="hasError.question"
        :error-msg="errorMsg"
        icon="mdi-heart-pulse"
        :title="$t(title.question)"
        :value="averageQuestion"
        :sub-text="numberOfQuestionsAnsweredString"
        :color="color"
      />
    </v-col>
  </v-row>
</template>

<script>
  /* Les composants et packages dont on a besoin */
  import StatsTemplate from './templates/StatsTemplate.vue'

  /* Rend le composant accessible aux autres composants */
  export default {
    /* Nom du composant */
    name: 'StatsMeasure',

    /* Sous-composants utilisés par ce composant */
    components: {
      StatsTemplate,
    },

    /* Paramètres */
    props: {
      request: {
        type: String,
        default: '',
      },
      isAdmin: {
        type: Boolean,
        default: false,
      },
      errorMsg: {
        type: String,
        default: 'Erreur',
      },
      gridSpacing: {
        type: Object,
        default: function () {
          return {
            xl: '4',
            lg: '4',
            md: '6',
            sm: '12',
            xs: '12',
          }
        },
      },
      color: {
        type: String,
        default: 'primary',
      },
    },

    /* Variables internes
     * data doit être une fonction si ce composant est utilisé dans un autre composant */
    data: function () {
      return {
        title: {
          question: 'Stats.Measure.Question.Title',
          mood: 'Stats.Measure.Mood.Title',
          superSmiles: 'Stats.Measure.SuperSmiles.Title',
        },
        averageQuestion: undefined,
        numberOfQuestionsAnswered: undefined,
        averageMood: undefined,
        numberOfMoodAnswered: undefined,
        numberOfSuperSmilesSent: undefined,
        isLoading: {
          question: true,
          mood: true,
          superSmiles: true,
        },
        hasError: {
          question: false,
          mood: false,
          superSmiles: false,
        },
      }
    },

    /* Variables internes dynamiques
     * (MAJ automatique quand les variables qu'elles utilisent sont mises à jour) */
    computed: {
      numberOfQuestionsAnsweredString: function () {
        const str = 'Stats.Measure.Question.Subtext'
        return (this.numberOfQuestionsAnswered
          ? this.$tc(str, this.numberOfQuestionsAnswered)
          : this.$tc(str, 1, { n: 0 }))
      },

      numberOfMoodAnsweredString: function () {
        const str = 'Stats.Measure.Mood.Subtext'
        return (this.numberOfMoodAnswered
          ? this.$tc(str, this.numberOfMoodAnswered)
          : this.$tc(str, 1, { n: 0 }))
      },

      numberOfSuperSmilesSentTitle: function () {
        return this.$tc(this.title.superSmiles,
                        (this.numberOfSuperSmilesSent
                          ? 1
                          : 2),
        )
      },
    },

    /* Variables surveillées : traitement si modification de sa valeur */
    watch: {
      request: function (newVal, oldVal) {
        this.fetchAllStats()
      },
    },

    /* Hook : fonction appelée juste après que l’instance ait été montée */
    mounted: function () {
      this.fetchAllStats()
    },

    /* Fonctions internes */
    methods: {
      fetchAllStats: function () {
        this.isLoading.question = true
        this.isLoading.mood = true
        this.isLoading.superSmiles = true
        this.fetchAverageScore()
        this.fetchCountStats('answers')
        this.fetchCountStats('mood')
        this.fetchCountStats('superSmiles')
      },

      // Récupération des données statistiques
      fetchCountStats: function (type) {
        let url = ''
        switch (type) {
          case 'answers':
            url = 'resultsByQuestions'
            break
          case 'mood':
            url = 'averageMoodThisWeek'
            break
          case 'superSmiles':
            url = 'selectSuperSmilesCount'
            break
        }
        if (url.length) {
          // console.log('url a fetch pour chiffre clés bien être ressentis : api/dashboard/count/' + url + this.request)
          this.$Auth.httpRequest(
            'get',
            'api/dashboard/count/' + url + this.request,
          ).then(datas => {
            switch (type) {
              case 'answers':
                this.numberOfQuestionsAnswered = datas.data[0].count
                break

              case 'mood':
                this.numberOfMoodAnswered = datas.data[0].count
                this.averageMood = datas.data[0].avgMood
                this.hasError.mood = false
                break

              case 'superSmiles':
                this.numberOfSuperSmilesSent = datas.data[0].count
                this.hasError.superSmiles = false
                break
            }
          }).catch((error) => {
            switch (type) {
              case 'answers':
                this.numberOfQuestionsAnswered = undefined
                break

              case 'mood':
                this.numberOfMoodAnswered = undefined
                this.averageMood = undefined
                break

              case 'superSmiles':
                this.numberOfSuperSmilesSent = undefined
                break
            }
            this.hasError.mood = true
            this.hasError.superSmiles = true
            /* if (this.isAdmin) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            } */
            this.$emit('set-error', error)
          }).finally(() => {
            switch (type) {
              case 'superSmiles':
                this.isLoading.superSmiles = false
                break

              case 'answers':
              case 'mood':
                this.isLoading.mood = false
                break
            }
          })
        }
      },

      // Récupération du score moyen
      fetchAverageScore: function () {
        this.$Auth.httpRequest(
          'get',
          'api/dashboard/averageScore' + this.request,
        ).then(datas => {
          this.hasError.question = false
          this.averageQuestion = datas.data[0].count
        }).catch((error) => {
          this.averageQuestion = undefined
          this.hasError.question = true
          /* if (this.isAdmin) {
            // console.log('Error in ' + this.$options.name + ' : ', error)
          } */
          this.$emit('set-error', error)
        }).finally(() => {
          this.isLoading.question = false
        })
      },
    },
  }
</script>
